const toNumberOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const parseKeywordBuckets = (rawData) =>
  (rawData || []).map((entry) => {
    const keyword = Object.keys(entry || {})[0];
    const list = (keyword && entry[keyword]) || [];
    const byId = {};
    list.forEach((inst) => {
      if (inst?.institution_id == null) return;
      byId[inst.institution_id] = inst;
    });
    return { keyword, list, byId };
  });

const getInstitutionNameMap = (buckets) => {
  const map = {};
  buckets.forEach(({ list }) => {
    list.forEach((inst) => {
      if (inst?.institution_id == null) return;
      if (!map[inst.institution_id]) {
        map[inst.institution_id] = inst.name || String(inst.institution_id);
      }
    });
  });
  return map;
};

export const getOrderedInstitutions = (rawData, institutionIds) => {
  const buckets = parseKeywordBuckets(rawData);
  const nameMap = getInstitutionNameMap(buckets);
  const ordered = (institutionIds || [])
    .filter((id) => nameMap[id])
    .map((id) => ({
      institution_id: id,
      name: nameMap[id] || String(id),
    }));
  return { buckets, ordered, nameMap };
};

export const buildKeywordInstitutionMatrix = (rawData, institutionIds, metricKey) => {
  const { buckets, ordered } = getOrderedInstitutions(rawData, institutionIds);
  const keywords = buckets.map((b) => b.keyword).filter(Boolean);

  let minValue = Number.POSITIVE_INFINITY;
  let maxValue = Number.NEGATIVE_INFINITY;
  const values = [];

  const matrixRows = buckets.map((bucket, rowIdx) => {
    const row = {
      keyword: bucket.keyword,
      values: ordered.map((inst, colIdx) => {
        const n = toNumberOrNull(bucket.byId[inst.institution_id]?.[metricKey]);
        if (n != null) {
          values.push([colIdx, rowIdx, n]);
          if (n < minValue) minValue = n;
          if (n > maxValue) maxValue = n;
        }
        return {
          institution_id: inst.institution_id,
          institution_name: inst.name,
          value: n,
        };
      }),
    };
    return row;
  });

  return {
    keywords,
    institutions: ordered,
    matrixRows,
    values,
    minValue: Number.isFinite(minValue) ? minValue : 0,
    maxValue: Number.isFinite(maxValue) ? maxValue : 0,
  };
};

export const buildInstitutionRankSeries = (rawData, institutionIds, metricKey) => {
  const { buckets, ordered } = getOrderedInstitutions(rawData, institutionIds);
  const keywords = buckets.map((b) => b.keyword).filter(Boolean);

  const lines = ordered.map((inst) => ({
    institution_id: inst.institution_id,
    name: inst.name,
    ranks: [],
    values: [],
  }));
  const byId = {};
  lines.forEach((line) => {
    byId[line.institution_id] = line;
  });

  let maxRank = 1;

  buckets.forEach((bucket) => {
    const rankCandidates = ordered
      .map((inst) => {
        const v = toNumberOrNull(bucket.byId[inst.institution_id]?.[metricKey]);
        return { id: inst.institution_id, value: v };
      })
      .filter((x) => x.value != null)
      .sort((a, b) => b.value - a.value);

    const rankMap = {};
    let currentRank = 0;
    let prevValue = null;
    rankCandidates.forEach((item, idx) => {
      if (idx === 0 || item.value !== prevValue) {
        currentRank = idx + 1;
      }
      rankMap[item.id] = currentRank;
      prevValue = item.value;
    });
    maxRank = Math.max(maxRank, rankCandidates.length || 1);

    ordered.forEach((inst) => {
      const line = byId[inst.institution_id];
      const raw = toNumberOrNull(bucket.byId[inst.institution_id]?.[metricKey]);
      line.values.push(raw);
      line.ranks.push(rankMap[inst.institution_id] ?? null);
    });
  });

  return { keywords, lines, maxRank };
};

export const buildInstitutionMeanMinMax = (rawData, institutionIds, metricKey) => {
  const { buckets, ordered } = getOrderedInstitutions(rawData, institutionIds);

  const rows = ordered.map((inst) => {
    const values = buckets
      .map((bucket) => toNumberOrNull(bucket.byId[inst.institution_id]?.[metricKey]))
      .filter((v) => v != null);

    if (!values.length) {
      return {
        institution_id: inst.institution_id,
        name: inst.name,
        count: 0,
        mean: null,
        min: null,
        max: null,
      };
    }

    const sum = values.reduce((acc, v) => acc + v, 0);
    return {
      institution_id: inst.institution_id,
      name: inst.name,
      count: values.length,
      mean: sum / values.length,
      min: Math.min(...values),
      max: Math.max(...values),
    };
  });

  const maxValue = rows.reduce((acc, row) => {
    if (row.max == null) return acc;
    return Math.max(acc, row.max);
  }, 0);

  return { rows, maxValue };
};

/**
 * Output-vs-impact bubble points:
 *   x    = scholarly output (publication_count)
 *   y    = citations per publication (citation_count / publication_count)
 *   size = h-index
 */
export const buildInstitutionScatterPoints = (benchmarkRows) => {
  const points = (benchmarkRows || []).map((row) => {
    const publications = toNumberOrNull(row.publication_count) || 0;
    const citations = toNumberOrNull(row.citation_count) || 0;
    return {
      institution_id: row.institution_id,
      name: row.name,
      x: publications,
      y: publications > 0 ? citations / publications : 0,
      size: toNumberOrNull(row.h_index) || 0,
    };
  });

  const domain = points.reduce(
    (acc, p) => ({
      xMin: Math.min(acc.xMin, p.x),
      xMax: Math.max(acc.xMax, p.x),
      yMin: Math.min(acc.yMin, p.y),
      yMax: Math.max(acc.yMax, p.y),
      sMin: Math.min(acc.sMin, p.size),
      sMax: Math.max(acc.sMax, p.size),
    }),
    {
      xMin: Number.POSITIVE_INFINITY,
      xMax: Number.NEGATIVE_INFINITY,
      yMin: Number.POSITIVE_INFINITY,
      yMax: Number.NEGATIVE_INFINITY,
      sMin: Number.POSITIVE_INFINITY,
      sMax: Number.NEGATIVE_INFINITY,
    }
  );

  return {
    points,
    domain: {
      xMin: Number.isFinite(domain.xMin) ? domain.xMin : 0,
      xMax: Number.isFinite(domain.xMax) ? domain.xMax : 0,
      yMin: Number.isFinite(domain.yMin) ? domain.yMin : 0,
      yMax: Number.isFinite(domain.yMax) ? domain.yMax : 0,
      sMin: Number.isFinite(domain.sMin) ? domain.sMin : 0,
      sMax: Number.isFinite(domain.sMax) ? domain.sMax : 0,
    },
  };
};
